import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, map, catchError, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { EncodingService } from './encoding.service';
import { TimezoneService } from './timezone.service';
import { environment } from '../../environments/environment';
@Injectable({
  providedIn: 'root'
})

export class DatabaseService {


  //baseURL = 'http://localhost:3000';
  baseURLLocal = 'http://localhost:3000';
  baseURL = environment.backend_base_url; // Base URL for the backend


  constructor(
    public _http: HttpClient,
    private encodingService: EncodingService,
    private timezoneService: TimezoneService
  ) { }

  private getGmidFromUserPrincipalName(userPrincipalName?: string): string {
    if (!userPrincipalName) return '';
    const local = userPrincipalName.split('@')[0]?.trim();
    if (!local) return '';
    return local.substring(0, 6).toLowerCase();
  }

  private normalizeGmid(value?: string): string {
    if (!value) return '';
    const trimmed = value.trim();
    if (!trimmed || trimmed.includes('@')) return '';
    return trimmed.substring(0, 6).toLowerCase();
  }

  private deserializePersonField(value: any): { gmid?: string; email?: string; name?: string } | null {
    if (!value) return null;
    if (typeof value === 'object') return value;

    const raw = String(value).trim();
    if (!raw) return null;

    if (raw.startsWith('{') && raw.endsWith('}')) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          return {
            gmid: this.normalizeGmid(parsed.gmid) || this.getGmidFromUserPrincipalName(parsed.userPrincipalName),
            email: parsed.email || parsed.mail || '',
            name: parsed.name || parsed.displayName || ''
          };
        }
      } catch {
        // Fall through to raw string handling
      }
    }

    return {
      gmid: this.normalizeGmid(raw),
      email: raw.includes('@') ? raw : '',
      name: raw.includes('@') ? '' : raw
    };
  }

  private serializePersonField(value: any): string {
    if (!value) return '';
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return '';
      if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
        try {
          const parsed = JSON.parse(trimmed);
          return JSON.stringify({
            gmid: this.normalizeGmid(parsed.gmid) || this.getGmidFromUserPrincipalName(parsed.userPrincipalName),
            email: parsed.email || parsed.mail || '',
            name: parsed.name || parsed.displayName || ''
          });
        } catch {
          // fall through to wrapping raw string
        }
      }
      const email = trimmed.includes('@') ? trimmed : '';
      const name = trimmed.includes('@') ? '' : trimmed;
      return JSON.stringify({
        gmid: this.normalizeGmid(trimmed),
        email,
        name
      });
    }

    if (typeof value === 'object') {
      return JSON.stringify({
        gmid: this.normalizeGmid(value.gmid) || this.getGmidFromUserPrincipalName(value.userPrincipalName),
        email: value.email || value.mail || value.userPrincipalName || '',
        name: value.name || value.displayName || ''
      });
    }

    return '';
  }

  private normalizeEventPeople(event: any): any {
    if (!event || typeof event !== 'object') return event;
    event.requestor = this.deserializePersonField(event.requestor);
    event.event_leader = this.deserializePersonField(event.event_leader);
    return event;
  }

  private isExcludedRequestor(event: any): boolean {
    const requestor = event?.requestor;
    if (!requestor || typeof requestor !== 'object') return false;

    const email = String(requestor.email || '').trim().toLowerCase();
    const name = String(requestor.name || requestor.displayName || '').trim().toLowerCase();
    const gmid = String(requestor.gmid || '').trim().toLowerCase();
    const requestorValue = `${email} ${name} ${gmid}`;

    return email === 'securityassurance.testing@gm.com' ||
      name === 'security assurance testing' ||
      requestorValue.includes('_solver_') ||
      /^\d+$/.test(name) ||
      name.startsWith('{') ||
      name.startsWith('<');
  }

  private normalizeAndFilterEvents(events: any[]): any[] {
    return events
      .map((event: any) => this.normalizeEventPeople(this.encodingService.decodeObject(event)))
      .filter((event: any) => !this.isExcludedRequestor(event));
  }

  private prepareEventPeopleForSave(event: any): any {
    if (!event || typeof event !== 'object') return event;
    const updated = { ...event };
    updated.requestor = this.serializePersonField(updated.requestor);
    updated.event_leader = this.serializePersonField(updated.event_leader);
    return updated;
  }

  
  /**
   * Get an event by its ID
   * @param http - The HttpClient instance
   * @param event_id - The ID of the event to retrieve
   * @returns Observable containing the event data
   */
  getEventById(event_id: string): Observable<any> {
    const url = `${this.baseURL}/api/request/${event_id}`;
    return this._http.get<any>(url).pipe(
      map(response => {
        if (response) {
          response = this.encodingService.decodeObject(response);
          response = this.normalizeEventPeople(response);
          this.timezoneService.eventToEasternTime(response);
          return response;
        }
      })
    );
  }

  /**
   * Create a new event
   * @param http - The HttpClient instance
   * @param eventData - The data for the new event
   * @returns Observable containing the response from the backend
   */
  createEvent(eventData: any): Observable<any> {
    const url = `${this.baseURL}/api/request/create`;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const prepared = this.prepareEventPeopleForSave(eventData);
    const encodedData = this.encodingService.encodeObject(prepared);
    console.log("Encoded version of the create event data:", encodedData);
    return this._http.post<any>(url, encodedData, { headers }).pipe(
      map(response => this.encodingService.decodeObject(response))
    );
  }

  /**
   * Update an event by its ID
   * @param http - The HttpClient instance
   * @param event_id - The ID of the event to update
   * @param eventData - The updated data for the event
   * @returns Observable containing the response from the backend
   */
  updateEventById(event_id: string, eventData: any): Observable<any> {
    const url = `${this.baseURL}/api/request/update/${event_id}`;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const prepared = this.prepareEventPeopleForSave(eventData);
    const encodedData = this.encodingService.encodeObject(prepared);
    console.log("Encoded version of the update event data:", encodedData);
    return this._http.post<any>(url, encodedData, { headers }).pipe(
      map(response => this.encodingService.decodeObject(response))
    );
  }

  /**
   * Quick status update for admin approval/rejection
   * @param event_id - The ID of the event to update
   * @param status - The new status ('Approved', 'Rejected', etc.)
   * @param workflow - workflow update
   * @returns Observable containing the response from the backend
   */
  updateEventStatus(event_id: string, status: string, workflow: string): Observable<any> {
    const url = `${this.baseURL}/api/request/status/${event_id}`;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const updateData = {
      request_status: status,
      workflow: workflow
    };
    return this._http.post<any>(url, updateData, { headers });
  }

  /**
   * Deletes an event by its ID
   * @param http - The HttpClient instance
   * @param event_id - The ID of the event to delete
   * @returns 
   */
  deleteEventById(event_id: string): Observable<any> {
    const url = `${this.baseURL}/api/request/delete/${event_id}`;
    return this._http.delete<any>(url);
  }

  /**
   * Get the list of all admins
   * @param http - The HttpClient instance
   * @returns Observable containing the list of all admins
   */
  getAllAdmins(): Observable<any> {
    const url = `${this.baseURL}/api/admin/all`;
    return this._http.get<any>(url);
  }

  /* -------------------- Event History (Versioning) -------------------- */
  /** Normalized event history entry */
  normalizeHistoryRow(row: any) {
    if (!row) return row;
    return {
      eventId: row.event_id || row.eventId,
      field: row.field,
      oldValue: row.oldValue ?? row.old_value ?? null,
      newValue: row.newValue ?? row.new_value ?? null,
      modifiedBy: row.modifiedBy ?? row.modified_by ?? null,
      createdAt: row.createdAt ?? row.created_at ?? null
    };
  }

  /** Retrieve flat history from new unified endpoint */
  getEventHistory(event_id: string, opts: { limit?: number; offset?: number } = {}): Observable<any[]> {
    const params: string[] = [];
    if (opts.limit != null) params.push(`limit=${encodeURIComponent(String(opts.limit))}`);
    if (opts.offset != null) params.push(`offset=${encodeURIComponent(String(opts.offset))}`);
    const q = params.length ? `?${params.join('&')}` : '';
    const url = `${this.baseURL}/api/history/${encodeURIComponent(event_id)}${q}`;
    return this._http.get<any>(url).pipe(
      catchError(() => of({ history: [] })),
      map(res => {
        // TEMP DEBUG: remove after verifying backend payload
        try { console.debug('[getEventHistory] raw response', res); } catch {}
        const arr = Array.isArray(res) ? res : (res?.history || []);

        return arr.map((r: any) => this.encodingService.decodeObject(this.normalizeHistoryRow(r)));
      })
    );
  }

  /** Retrieve grouped revisions */
  getEventHistoryGrouped(eventId: string, opts: { limit?: number; offset?: number } = {}): Observable<any> {
    const params: string[] = [];
    if (opts.limit != null) params.push(`limit=${encodeURIComponent(String(opts.limit))}`);
    if (opts.offset != null) params.push(`offset=${encodeURIComponent(String(opts.offset))}`);
    const q = params.length ? `?${params.join('&')}` : '';
    const url = `${this.baseURL}/api/history/${encodeURIComponent(eventId)}/grouped${q}`;
    return this._http.get<any>(url);
  }

  /** Retrieve latest revision only */
  getEventHistoryLatest(eventId: string): Observable<any> {
    const url = `${this.baseURL}/api/history/${encodeURIComponent(eventId)}/latest`;
    return this._http.get<any>(url);
  }

  /** Retrieve the latest ENAC pull data for the Health tab */
  getEnacLastPull(): Observable<any> {
    const url = `${this.baseURL}/api/enac/last-poll`;
    return this._http.get<any>(url);
  }

  /** No-op: server handles history capture automatically */
  recordEventHistory(): Observable<any> {
    return new Observable(observer => { observer.next({ skipped: true, serverManaged: true }); observer.complete(); });
  }

  /**
   * Explicitly persist a batch of field-level changes for an event when the backend is NOT auto-diffing.
   * @param eventId Target event id
   * @param changes Array of change objects { field, oldValue, newValue, user, fieldLabel? }
   * @param opts Optional metadata (correlationId etc.)
   */
  postEventHistory(eventId: string, changes: Array<{field: string; oldValue: any; newValue: any; user: string; fieldLabel?: string}>, opts: { correlationId?: string } = {}): Observable<any> {
    if (!eventId || !changes?.length) {
      return of({ skipped: true, reason: 'no-event-or-no-changes' });
    }
    const url = `${this.baseURL}/api/history/${encodeURIComponent(eventId)}`; // POST to same base path
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const body = {
      event_id: eventId,
      correlation_id: opts.correlationId,
      changes: changes.map(c => ({
        field: c.field,
        field_label: c.fieldLabel || c.field,
        old_value: c.oldValue,
        new_value: c.newValue,
        // Provide multiple synonymous columns so whatever backend expects is populated
                  // generic
        gmid: c.user,            // if backend stores GMID separately
            // alternative legacy style
      }))
    };
    try { console.debug('[postEventHistory] payload', body); } catch {}
    return this._http.post<any>(url, body, { headers }).pipe(
      catchError(err => {
        try { console.error('[postEventHistory] failed', err); } catch {}
        return of({ success: false, error: true });
      })
    );
  }
  

  /**
   * Add new admins
   * @param http - The HttpClient instance
   * @param adminData - The data of the admins to be added. Needs gmid, name, email, and gmin
   * @returns Observable containing the response from the backend
   */
  addAdmins(adminData: any): Observable<any> {
    const url = `${this.baseURL}/api/admin/add`;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this._http.post<any>(url, adminData, { headers });
  }

  /**
   * Remove an admin
   * @param http - The HttpClient instance
   * @param gmid - The ID of the admin to be removed
   * @returns Observable containing the response from the backend
   */
  removeAdmin(gmid: string): Observable<any> {
    const url = `${this.baseURL}/api/admin/remove/${gmid}`;
    return this._http.delete<any>(url);
  }

  getAdminByGMID(gmid: string): Observable<any> {
    const url = `${this.baseURL}/api/admin/find/${gmid}`;
    return this._http.get<any>(url);
  }

  /**

   * Update an admin's scheduling team status
   * @param gmid The GMID of the admin
   * @param isSchedulingTeam The new scheduling team status
   * @returns Observable with the response
   */
  updateAdminSchedulingTeam(gmid: string, isSchedulingTeam: boolean): Observable<any> {
    const payload = {
      gmid: gmid,
      isSchedulingTeam: isSchedulingTeam
    };

    return this._http.post<any>(`${this.baseURL}/api/admin/scheduling`, payload);
  }

  /**
   * Update an admin's owner status
   * Assumes backend endpoint POST /api/admin/owner expects { gmid, isOwner }
   * @param gmid The GMID of the admin
   * @param isOwner New owner status
   */
  updateAdminOwner(gmid: string, isOwner: boolean): Observable<any> {
    const payload = { gmid, isOwner };
    // Using POST unless backend specifies PUT; 404 previously observed with PUT.
    return this._http.post<any>(`${this.baseURL}/api/admin/owner`, payload);
  }

  testTimeConverter(time: any) {
    console.log('Time zone convert to UTC:', this.timezoneService.easternDateTimeStringToUtcISO(time));
    console.log('Time zone convert back to ET:', this.timezoneService.utcISOToEasternDateTimeString(this.timezoneService.easternDateTimeStringToUtcISO(time)));
  }

  //generate the payload for the event creation or save for later
  generateEventPayload(formData: any, workflowStatus: string): any {
    console.log("Generating event payload with form data:", formData);
    const techValue = Array.isArray(formData.tech) ? formData.tech.join(', ') : formData.tech;
    const toIso = (d: any) => {
      if (!d) return '';
      try {
        if (d instanceof Date) return d.toISOString();
        const parsed = new Date(d);
        return isNaN(parsed.getTime()) ? '' : parsed.toISOString();
      } catch { return ''; }
    };
    const num = (v: any, fallback = 0) => {
      if (v === '' || v === undefined || v === null) return fallback;
      const n = Number(v);
      return isNaN(n) ? fallback : n;
    };
    const yesNo = (v: any, def: 'Yes' | 'No' = 'No') => {
      if (v === true || v === 'true' || v === 'Yes') return 'Yes';
      if (v === false || v === 'false' || v === 'No') return 'No';
      return def;
    };
    return {
      ...(formData.event_id && { event_id: formData.event_id }),
      workflow: workflowStatus,
      request_status: formData.requestStatus || "Pending",
      // User - Request Form
      request_type: formData.requestType,
      // Live Event
      request_title: formData.requestTitle,
      expected_number_of_participants: formData.expectedNumberOfParticipants || 0,
      start_date: this.timezoneService.easternDateTimeStringToUtcISO(formData.eventStartDate),
      end_date: this.timezoneService.easternDateTimeStringToUtcISO(formData.eventEndDate),
      primary_presenting_location: formData.primaryPresentingLocation || "",
      other_location: formData.otherLocation || "",
      recording_location: formData.recordingLocation || "",
      other_recording_location: formData.otherRecordingLocation || "",
      additional_detail_for_location: formData.additionalDetailForLocation || "",
      remote_presenting_location: formData.remotePresentingLocation || "",
      requestor: this.serializePersonField(formData.requestorMeta || formData.requestor || ""),
      event_leader: this.serializePersonField(formData.eventLeaderMeta || formData.eventLeader || ""),
      responsible_for_editing: formData.responsibleForEditing || "",
      responsible_for_editing_other: formData.responsibleForEditingOther || "",
      event_requirements: formData.eventRequirements || "",
      set_items: formData.setItems || "",
      event_description: formData.eventDescription || "",
      presenters: formData.presenters || "",
      audience: formData.audience || "",
      audience_other: formData.audienceOther || "",
      video_sharing_platforms: formData.videoSharingPlatforms || "",
      video_sharing_platforms_other: formData.videoSharingPlatformsOther || "",
      video_file_recipient: formData.videoFileRecipient || "",
      region: formData.region || "",
      how_will_you_take_questions: formData.takeQuestions || "",
      how_will_you_take_questions_other: formData.takeQuestionsOther || "",
      is_video_on_demand_needed: formData.isVideoOnDemandNeeded || "No",
      vod_expiration_date: this.timezoneService.easternDateTimeStringToUtcISO(formData.vodExpirationDate),
      data_classification: formData.dataClassification || "",
      retention_time: formData.retentionTime || "",
      // Recording
      // Special Request
      // Holiday
      // Admin - Broadcast Detail
      assigned_to: formData.assignedTo || "",
      tech: techValue || "",
      high_profile_event: yesNo(formData.highProfileEvent, 'No'),
      captioning: yesNo(formData.captioning, 'No'),
      work_related_event: yesNo(formData.workRelatedEvent, 'Yes'),
      testing: yesNo(formData.testing, 'No'),
      tools_to_be_used: formData.toolsToBeUsed || "",
      tools_other: formData.toolsOther || "",
      program_code: formData.programCode || "",
      cust_number: formData.custNumber || "",
      studio_needs: formData.studioNeeds || "",
      broadcast_comments: formData.broadcastComments || "",
      gm_video_url: formData.gmVideoUrl || "",
      virtual_events_url: formData.virtualEventsUrl || "",
      brightcove_url: formData.brightcoveUrl || "",
      teams_url: formData.teamsUrl || "",
      // Admin - Video Edit
      send_to_archive: yesNo(formData.sendToArchive, 'No'),
      caption_editor: formData.captionEditor || "",
      video_editing_assigned_to: formData.videoEditingAssignedTo || "",
      event_ownership_transferred: yesNo(formData.eventOwnershipTransferred, 'No'),
      // Admin - Metric Edit
      gm_video_attendees: num(formData.gmVideoAttendees, 0),
      venue_attendees: num(formData.venueAttendees, 0),
      brightcove_attendees: num(formData.brightcoveAttendees, 0),
      virtual_events_attendees: num(formData.virtualEventsAttendees, 0),
      teams_attendees: num(formData.teamsAttendees, 0),
      social_media_attendees: num(formData.socialMediaAttendees, 0),
      audio_conference_attendees: num(formData.audioConferenceAttendees, 0),
      gmvc_room_count: num(formData.gmvcRoomCount, 0),
      studio_services_used: formData.studioServicesUsed || "No",
      // slack_attendees: formData.slackAttendees || 0,
    };
  }

  /**
   * Get all events from the database
   * @param http - The HttpClient instance
   * @returns Observable containing all events
   */
  getAllEvents(): Observable<any> {
    const url = `${this.baseURL}/api/request/all`;
    return this._http.get<any>(url).pipe(
      map(response => {
        if (response.events && Array.isArray(response.events)) {
          response.events = this.normalizeAndFilterEvents(response.events);
          response.events.forEach((event: any) => this.timezoneService.eventToEasternTime(event));
        }
        return response;
      })
    );
  }

  /**
   * Get events by date range
   * @param http - The HttpClient instance
   * @param startDate - Start date in YYYY-MM-DD format
   * @param endDate - End date in YYYY-MM-DD format
   * @returns Observable containing events in the date range
   */
  getEventsByDateRange(startDate: string, endDate: string): Observable<any> {
    const url = `${this.baseURL}/api/getEventsByDateRange?startDate=${startDate}&endDate=${endDate}`;
    return this._http.get<any>(url).pipe(
      map(response => {
        if (response.events && Array.isArray(response.events)) {
          response.events = this.normalizeAndFilterEvents(response.events);
          response.events.forEach((event: any) => this.timezoneService.eventToEasternTime(event));
        }
        return response.events || [];
      })
    );
  }

  /**
   * Add a new holiday event
   * @param holidayData - The data for the new holiday event
   * @returns Observable containing the response from the backend
   */
  addHolidayEvent(holidayData: any): Observable<any> {
    const url = `${this.baseURL}/api/holidays/add`;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this._http.post<any>(url, holidayData, { headers });
  }

  /**
   * Delete a holiday event by its ID
   * @param holidayId - The ID of the holiday event to delete
   * @returns Observable containing the response from the backend
   */
  deleteHolidayEvent(holidayId: string): Observable<any> {
    const url = `${this.baseURL}/api/holidays/${holidayId}`;
    return this._http.delete<any>(url);
  }

  /**
   * Get all holiday events
   * @returns Observable containing the list of holiday events
   */
  getHolidayEvents(): Observable<any> {
    const url = `${this.baseURL}/api/holidays/all`;
    return this._http.get<any>(url);
  }
  
  getAllViewers() {
    const url = `${this.baseURL}/api/viewer/all`;
    return this._http.get<any>(url);
  }

  addViewer(viewerData: any): Observable<any> {
    const url = `${this.baseURL}/api/viewer/add`;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this._http.post<any>(url, viewerData, { headers });
  }

  removeViewer(gmid: string): Observable<any> {
    const url = `${this.baseURL}/api/viewer/${gmid}`;
    return this._http.delete<any>(url);
  }
  /**
   * Upload migration events from CSV
   * @param events - Array of events to upload
   * @returns Observable containing the response from the backend
   */
  uploadMigrationEvents(events: any[]): Observable<any> {
    const url = `${this.baseURL}/api/migration/events`;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this._http.post<any>(url, { events }, { headers });
  }
}

